import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Handshake, Plus, Pencil, Trash2, Loader2, Eye, EyeOff,
  Building2, User, Mail, Phone, Percent, CheckCircle
} from 'lucide-react';

const EMPTY_FORM = {
  company_name: '', name: '', email: '', phone: '',
  password: '', status: 'active',
  monthly_commission_rate: '25', tx_commission_rate: '5',
  upline_commission_rate: '5', referred_by: '',
};

export default function AdminPartners() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/partners`, { headers });
      setPartners(data || []);
    } catch {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowPw(false);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      company_name: p.company_name || '',
      name: p.name || '',
      email: p.email || '',
      phone: p.phone || '',
      password: '',
      status: p.status || 'active',
      monthly_commission_rate: p.monthly_commission_rate ?? p.commission ?? '25',
      tx_commission_rate: p.tx_commission_rate ?? '5',
      upline_commission_rate: p.upline_commission_rate ?? '5',
      referred_by: p.referred_by || '',
    });
    setShowPw(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error('Company name is required'); return; }
    if (!form.name.trim()) { toast.error('Contact name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!editing && !form.password) { toast.error('Password is required for new partners'); return; }
    setSaving(true);
    try {
      const payload = { ...form, monthly_commission_rate: parseFloat(form.monthly_commission_rate) || 0, tx_commission_rate: parseFloat(form.tx_commission_rate) || 0 };
      if (editing) {
        if (!payload.password) delete payload.password;
        const { data } = await axios.put(`${API}/admin/partners/${editing.id}`, payload, { headers });
        setPartners(ps => ps.map(p => p.id === editing.id ? data : p));
        toast.success('Partner updated');
      } else {
        const { data } = await axios.post(`${API}/admin/partners`, payload, { headers });
        setPartners(ps => [data, ...ps]);
        toast.success('Partner created');
      }
      setModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/partners/${deleteTarget.id}`, { headers });
      setPartners(ps => ps.filter(p => p.id !== deleteTarget.id));
      toast.success(`Partner "${deleteTarget.company_name}" deleted`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete partner');
    } finally {
      setDeleting(false);
    }
  };

  const f = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <AdminLayout title="Partners">
      <div className="space-y-5" data-testid="admin-partners-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{partners.length} partner{partners.length !== 1 ? 's' : ''} registered</p>
          </div>
          <Button onClick={openAdd} className="gap-2" data-testid="add-partner-btn">
            <Plus className="h-4 w-4" /> Add Partner
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : partners.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Handshake className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-muted-foreground">No partners yet. Add your first partner.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="partners-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Company Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Contact Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Phone</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Commission</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Referral Code</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {partners.map(p => (
                      <tr key={p.id} className="hover:bg-muted/20 transition-colors" data-testid={`partner-row-${p.id}`}>
                        {/* Company */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Building2 className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground leading-tight">{p.company_name}</p>
                              <p className="text-xs text-muted-foreground">{p.email}</p>
                            </div>
                          </div>
                        </td>
                        {/* Name */}
                        <td className="px-4 py-3 text-foreground">{p.name}</td>
                        {/* Phone */}
                        <td className="px-4 py-3 text-muted-foreground text-[13px]">{p.phone || '—'}</td>
                        {/* Commission */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold border border-blue-500/20">
                              <Percent className="h-3 w-3" />{p.monthly_commission_rate ?? p.commission ?? 0}% Monthly
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 text-xs font-semibold border border-purple-500/20">
                              <Percent className="h-3 w-3" />{p.tx_commission_rate ?? 0}% TX
                            </span>
                          </div>
                        </td>
                        {/* Referral Code */}
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono bg-muted/60 px-2 py-1 rounded text-muted-foreground">
                            {p.referral_code || '—'}
                          </code>
                        </td>
                        {/* Status */}
                        <td className="px-4 py-3">
                          {p.status === 'active' ? (
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[11px]">Active</Badge>
                          ) : (
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20 text-[11px]">Inactive</Badge>
                          )}
                        </td>
                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 px-2 border-border"
                              onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline"
                              className="h-7 px-2 border-red-500/20 text-red-500 hover:bg-red-500/10"
                              onClick={() => setDeleteTarget(p)} data-testid={`delete-partner-${p.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="partner-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-primary" />
              {editing ? 'Edit Partner' : 'Add Partner'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-1">
            {/* Company Name */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> Company Name <span className="text-red-400">*</span>
              </Label>
              <Input value={form.company_name} onChange={f('company_name')}
                placeholder="Acme Corp" data-testid="partner-company-name" />
            </div>

            {/* Contact Name */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-muted-foreground" /> Contact Name <span className="text-red-400">*</span>
              </Label>
              <Input value={form.name} onChange={f('name')}
                placeholder="John Doe" data-testid="partner-name" />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5 text-muted-foreground" /> Email <span className="text-red-400">*</span>
              </Label>
              <Input type="email" value={form.email} onChange={f('email')}
                placeholder="partner@company.com" data-testid="partner-email" />
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" /> Phone Number
              </Label>
              <Input value={form.phone} onChange={f('phone')}
                placeholder="+1 234 567 8900" data-testid="partner-phone" />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                Password {!editing && <span className="text-red-400">*</span>}
              </Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={f('password')}
                  placeholder={editing ? 'Leave blank to keep current' : 'Min 6 characters'}
                  className="pr-10"
                  data-testid="partner-password"
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {editing && <p className="text-xs text-muted-foreground">Leave blank to keep current password.</p>}
            </div>

            {/* Monthly Commission + TX Commission */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Percent className="h-3.5 w-3.5 text-muted-foreground" /> Monthly Commission (%)
                </Label>
                <Input type="number" min="0" max="100" step="0.1"
                  value={form.monthly_commission_rate} onChange={f('monthly_commission_rate')}
                  placeholder="25" data-testid="partner-monthly-commission" />
                <p className="text-[10px] text-muted-foreground">On subscription fees</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Percent className="h-3.5 w-3.5 text-muted-foreground" /> TX Commission (%)
                </Label>
                <Input type="number" min="0" max="100" step="0.1"
                  value={form.tx_commission_rate} onChange={f('tx_commission_rate')}
                  placeholder="5" data-testid="partner-tx-commission" />
                <p className="text-[10px] text-muted-foreground">On transaction fees</p>
              </div>
            </div>

            {/* Upline Commission + Status */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Upline Commission (%)</Label>
                <Input type="number" min="0" max="100" step="0.1"
                  value={form.upline_commission_rate} onChange={f('upline_commission_rate')}
                  placeholder="5" data-testid="partner-upline-commission" />
                <p className="text-[10px] text-muted-foreground">Passed to upline partner</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={f('status')} data-testid="partner-status"
                  className="w-full h-10 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            {/* Upline Partner */}
            <div className="space-y-1.5">
              <Label className="text-xs">Upline Partner (optional)</Label>
              <select value={form.referred_by} onChange={f('referred_by')} data-testid="partner-referred-by"
                className="w-full h-10 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                <option value="">None (top-level)</option>
                {partners.filter(p => !editing || p.id !== editing.id).map(p => (
                  <option key={p.id} value={p.id}>{p.company_name} — {p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-white" data-testid="save-partner-btn">
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                : <CheckCircle className="h-4 w-4 mr-1.5" />}
              {editing ? 'Save Changes' : 'Create Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-partner-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Partner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.company_name}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-partner-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
